import { Handle, Position } from '@xyflow/react';
import { Stack, NumberInput } from '@mantine/core';
import { IconUserSearch } from '@tabler/icons-react';
import { WidgetNodeShell } from '../../components/flow-editor/nodes/widget-node-shell';
import { LabeledHandle } from '../../components/flow-editor/nodes/labeled-handle';
import { VariableTextarea } from '../../components/flow-editor/variable-textarea';
import { RephraseEditor } from '../../components/flow-editor/rephrase-editor';
import type { WidgetDefinition, WidgetNodeProps, ConfigPanelProps } from '../types';
import { getCustomerIdWidgetCore, type GetCustomerIdConfig } from '@legacy-shared/widgets/core/getCustomerId';

export { type GetCustomerIdConfig } from '@legacy-shared/widgets/core/getCustomerId';

function GetCustomerIdNode({ id, data, selected }: WidgetNodeProps<GetCustomerIdConfig>) {
  return (
    <WidgetNodeShell
      icon={IconUserSearch}
      title={data.label || 'Get Customer ID'}
      subtitle="customerId"
      configured={!!data.config.prompt && (data.config.retryCount === 0 || !!data.config.retryPrompt)}
      errors={data.errors}
      selected={selected}
      color="cyan"
      retryCount={data.config.retryCount}
    >
      <Handle type="target" position={Position.Top} id="in" />

      <LabeledHandle type="source" position="bottom" id="noMatch" label="No Match" offset="35%" nodeId={id} />

      <LabeledHandle type="source" position="bottom" id="match" label="Match" offset="65%" nodeId={id} />
    </WidgetNodeShell>
  );
}

function PromptPanel({
  config,
  onChange,
  availableVariables,
  languages,
  defaultLanguage,
}: ConfigPanelProps<GetCustomerIdConfig>) {
  return (
    <Stack gap="xs" style={{ flex: 1 }}>
      <VariableTextarea
        label="Prompt"
        description="What the caller hears when asked to enter their customer ID"
        placeholder="Please enter your customer ID followed by the pound key."
        value={config.prompt}
        onChange={(prompt) => onChange({ ...config, prompt })}
        availableVariables={availableVariables}
        languages={languages}
        defaultLanguage={defaultLanguage}
        minRows={3}
        style={{ flex: 1 }}
        styles={{
          root: { display: 'flex', flexDirection: 'column', flex: 1 },
          wrapper: { flex: 1 },
          input: { height: '100%' },
        }}
      />
      <RephraseEditor
        message={config.prompt}
        rephrases={config.promptRephrases}
        onChange={(promptRephrases) => onChange({ ...config, promptRephrases })}
        languages={languages}
        defaultLanguage={defaultLanguage}
      />
    </Stack>
  );
}

function RetryPanel({
  config,
  onChange,
  availableVariables,
  languages,
  defaultLanguage,
}: ConfigPanelProps<GetCustomerIdConfig>) {
  return (
    <Stack gap="md" style={{ flex: 1 }}>
      <NumberInput
        label="Retry Count"
        description="Number of times to retry before routing to No Match"
        value={config.retryCount}
        onChange={(value) => onChange({ ...config, retryCount: typeof value === 'number' ? value : 2 })}
        min={0}
        max={5}
      />
      <NumberInput
        label="Input Timeout"
        description="How long to wait for the caller to enter digits"
        value={config.timeoutSeconds ?? 10}
        onChange={(value) => onChange({ ...config, timeoutSeconds: typeof value === 'number' ? value : 10 })}
        min={1}
        max={60}
        suffix=" seconds"
      />
      <VariableTextarea
        label="Retry Prompt"
        description="What the caller hears when retrying after no input"
        placeholder="We did not receive your input. Please enter your customer ID followed by the pound key."
        value={config.retryPrompt}
        onChange={(retryPrompt) => onChange({ ...config, retryPrompt })}
        availableVariables={availableVariables}
        languages={languages}
        defaultLanguage={defaultLanguage}
        minRows={3}
        style={{ flex: 1 }}
        styles={{
          root: { display: 'flex', flexDirection: 'column', flex: 1 },
          wrapper: { flex: 1 },
          input: { height: '100%' },
        }}
      />
      <RephraseEditor
        message={config.retryPrompt}
        rephrases={config.retryPromptRephrases}
        onChange={(retryPromptRephrases) => onChange({ ...config, retryPromptRephrases })}
        languages={languages}
        defaultLanguage={defaultLanguage}
      />
    </Stack>
  );
}

export const getCustomerIdWidget: WidgetDefinition<GetCustomerIdConfig> = {
  ...getCustomerIdWidgetCore,
  icon: 'IconUserSearch',
  variables: getCustomerIdWidgetCore.variables,

  handles: {
    inputs: [{ id: 'in', type: 'default', label: 'Input', position: 'top' }],
    outputs: [
      { id: 'noMatch', type: 'nomatch', label: 'No Match', position: 'bottom' },
      { id: 'match', type: 'success', label: 'Match', position: 'bottom' },
    ],
  },

  connectionRules: {
    allowedInputFrom: ['*'],
    allowedOutputTo: ['*'],
  },

  ui: {
    component: GetCustomerIdNode,
    configPanel: PromptPanel,
    configTabs: [
      { value: 'prompt', label: 'Prompt', panel: PromptPanel },
      { value: 'retry', label: 'Retry', panel: RetryPanel },
    ],
    preview: () => 'customerId',
  },

  validation: {
    ...getCustomerIdWidgetCore.validation,
    validateConnections: () => ({ valid: true, errors: [] }),
  },
};
